/**
 * Opportunity Detail Screen Components
 * 
 * Small reusable components for the Opportunity Detail screen.
 * Extracted to keep main screen under 300 lines per cursor rules.
 */

import React from 'react';
import { View, Text, Pressable, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color } from '../../../shared/tokens/mobile';
import { getCountyRecordUrls } from '../../../shared/utils/formatters';
import { logger } from '../../../shared/utils/logger';
import { styles } from './opportunityDetailStyles';

/**
 * Metric display component for key stats.
 */
export const Metric = ({ value, label }: { value: string; label: string }) => (
  <View style={styles.metric}>
    <Text style={styles.metricValue}>{value}</Text>
    <Text style={styles.metricLabel}>{label}</Text>
  </View>
);

/**
 * Parcel detail row component.
 */
export const ParcelDetail = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.parcelDetailRow}>
    <Text style={styles.parcelDetailLabel}>{label}</Text>
    <Text style={styles.parcelDetailValue} numberOfLines={1}>{value}</Text>
  </View>
);

/**
 * County record links component.
 * Shows links to tax records, GIS maps, and NC OneMap for the county.
 */
export const CountyRecordLinks = ({ 
  county, 
  parcelId 
}: { 
  county?: string | null; 
  parcelId?: string | null;
}) => {
  const urls = getCountyRecordUrls(county, parcelId);
  const hasLinks = urls.taxUrl || urls.gisUrl;
  
  if (!hasLinks && !county) return null;
  
  const openUrl = (url: string) => {
    Linking.openURL(url).catch(err => logger.error('Failed to open URL', err, 'parcel'));
  };
  
  return (
    <View style={styles.countyLinksContainer}>
      <Text style={styles.countyLinksTitle}>County Records</Text>
      <View style={styles.countyLinksRow}>
        {urls.taxUrl && (
          <Pressable onPress={() => openUrl(urls.taxUrl!)} style={styles.countyLinkButton}>
            <Ionicons name="document-text-outline" size={14} color={color.info} />
            <Text style={styles.countyLinkText}>Tax Records</Text>
          </Pressable>
        )}
        {urls.gisUrl && (
          <Pressable onPress={() => openUrl(urls.gisUrl!)} style={styles.countyLinkButton}>
            <Ionicons name="map-outline" size={14} color={color.info} />
            <Text style={styles.countyLinkText}>GIS Map</Text>
          </Pressable>
        )}
        <Pressable onPress={() => openUrl(urls.ncOneMapUrl)} style={styles.countyLinkButton}>
          <Ionicons name="globe-outline" size={14} color={color.info} />
          <Text style={styles.countyLinkText}>NC OneMap</Text>
        </Pressable>
      </View>
    </View>
  );
};

